import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import cloudApi from "../../../../services/cloudApi";
import { formatBytes, formatNumber } from "../../../../utils/helpers";
import { cfThumbnail } from "../../../../utils/imageUtils";
import { AUTOMATION_TYPES, TYPE_STRING_TO_UI_KEY } from "../config/automationTypes";
import ImageGalleryModal from "./ImageGalleryModal";

const truncateUrl = (url) => {
  try {
    const parsed = new URL(url);
    const display = parsed.hostname.replace(/^www\./, '') + parsed.pathname;
    return display.length > 80 ? display.substring(0, 77) + '...' : display;
  } catch {
    return url?.length > 80 ? url.substring(0, 77) + '...' : url;
  }
};

const formatMs = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
};

// ── Activity feed row (memoized: skips re-render across polls when unchanged) ──
const ActivityRow = React.memo(({ item }) => {
  const isPending = item.status === 'pending' || item.status === 'retrying';
  const isInProgress = item.status === 'in_progress';
  const hasError = (item.status === 'failed' || item.status === 'blocked') && item.error;
  const subPages = item.status === 'completed' ? (item.scannedPages || []).filter((p) => p.depth > 0) : [];
  const hiddenSubPages = subPages.length > 0 ? (item.pagesScanned || 0) - 1 - subPages.length : 0;
  return (
    <div className={`py-1.5 px-2.5 rounded-lg ${
      isPending ? 'opacity-35' : isInProgress ? 'bg-blue-500/5 dark:bg-blue-500/5' : hasError ? 'bg-red-500/5 dark:bg-red-500/5' : ''
    }`}>
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0 w-4 flex justify-center">
          {item.status === 'completed' ? (
            <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          ) : isInProgress ? (
            <svg className="w-3.5 h-3.5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : item.status === 'blocked' ? (
            <svg className="w-3.5 h-3.5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : item.status === 'failed' ? (
            <svg className="w-3.5 h-3.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
          )}
        </div>
        <span className={`truncate font-mono text-xs flex-1 ${isPending ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}`}
          title={item.url}>
          {truncateUrl(item.url)}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.recoveredFrom && item.status === 'completed' && (
            <span className="inline-flex items-center gap-0.5 text-xs text-emerald-500"
              title={item.recoveredMessage || `Resolved ${item.recoveredLabel}`}>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              rescued
            </span>
          )}
          {item.blockerLabel && <span className="text-xs text-orange-500 dark:text-orange-400">{item.blockerLabel}</span>}
          {item.attempts > 1 && <span className="text-xs text-amber-500 tabular-nums">{item.attempts}x</span>}
          {item.duration != null && item.status === 'completed' && (
            <span className="text-xs font-mono text-gray-400 dark:text-gray-500 tabular-nums">{formatMs(item.duration)}</span>
          )}
          {item.httpCode && item.httpCode >= 400 && (
            <span className="text-xs font-mono text-red-400">{item.httpCode}</span>
          )}
          {isPending && <span className="text-xs text-gray-400 dark:text-gray-600 italic">queued</span>}
        </div>
      </div>
      {!isPending && (hasError || item.title) && (
        <p className={`ml-6 mt-0.5 text-xs truncate ${
          item.status === 'failed' ? 'text-red-500 dark:text-red-400'
          : item.status === 'blocked' ? 'text-orange-500 dark:text-orange-400'
          : 'text-gray-400 dark:text-gray-500'
        }`} title={item.error || item.title}>
          {item.error || item.title}
        </p>
      )}
      {subPages.length > 0 && (
        <div className="ml-6 mt-1 border-l border-gray-200/70 dark:border-white/[0.06] pl-3 space-y-0.5">
          {subPages.map((p, j) => (
            <div key={j} className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500" title={p.url}>
              <span className="text-gray-300 dark:text-gray-600">↳</span>
              <span className="truncate font-mono">{truncateUrl(p.url)}</span>
            </div>
          ))}
          {hiddenSubPages > 0 && (
            <div className="text-xs text-gray-400 dark:text-gray-600 italic">+{hiddenSubPages} more sub-page{hiddenSubPages !== 1 ? 's' : ''}</div>
          )}
        </div>
      )}
    </div>
  );
}, (a, b) => {
  // Each poll hands us a fresh object, so compare the fields that drive the render.
  // A stable completed row then skips re-render entirely while the feed updates around it.
  const x = a.item, y = b.item;
  return x.index === y.index && x.status === y.status && x.url === y.url
    && x.duration === y.duration && x.attempts === y.attempts && x.httpCode === y.httpCode
    && x.pagesScanned === y.pagesScanned && (x.scannedPages?.length || 0) === (y.scannedPages?.length || 0);
});

// ── Animated number counter ──────────────────────────────────────────
const AnimatedNumber = ({ value, format = (v) => v, duration = 700 }) => {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    if (value === prev.current) return;
    const start = prev.current;
    const diff = value - start;
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.round(start + diff * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    prev.current = value;
  }, [value, duration]);

  return <span>{format(display)}</span>;
};

// ── Animated SVG ring ────────────────────────────────────────────────
const ProgressRing = ({ percent, size = 52, strokeWidth = 3, color = "text-emerald-400", bgColor = "text-white/[0.06]" }) => {
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className={`-rotate-90`} width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor"
          className={bgColor} strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor"
          className={color} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
        {Math.round(percent)}%
      </span>
    </div>
  );
};

// ── Horizontal bar chart component ───────────────────────────────────
const HorizontalBar = ({ value, max, label, color = "from-blue-400 to-cyan-400", delay = 0 }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-500 dark:text-gray-400 w-20 text-right truncate font-mono">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-white/[0.04] overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${color} animate-bar-fill`}
          style={{ '--bar-width': `${Math.max(pct, 1)}%`, animationDelay: `${delay}ms` }}
        />
      </div>
    </div>
  );
};

// ── Section card wrapper ─────────────────────────────────────────────
const Section = ({ children, className = "", delay = 0 }) => (
  <div
    className={`bg-white dark:bg-white/[0.02] border border-gray-200/60 dark:border-white/[0.06] rounded-xl overflow-hidden animate-stagger-in ${className}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

// ── Section header ───────────────────────────────────────────────────
const SectionHeader = ({ children, right }) => (
  <div className="flex items-center justify-between px-5 pt-4 pb-3">
    <h4 className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{children}</h4>
    {right}
  </div>
);

// ── Status config ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  success: {
    label: 'Completed',
    gradient: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-500',
    ring: 'ring-emerald-500/20',
    glow: 'shadow-[0_0_20px_rgba(52,211,153,0.15)]',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  failed: {
    label: 'Failed',
    gradient: 'from-red-400 to-rose-500',
    bg: 'bg-red-500/10',
    text: 'text-red-500',
    ring: 'ring-red-500/20',
    glow: 'shadow-[0_0_20px_rgba(239,68,68,0.15)]',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  },
  stopped: {
    label: 'Stopped',
    gradient: 'from-gray-400 to-gray-500',
    bg: 'bg-gray-500/10',
    text: 'text-gray-400',
    ring: 'ring-gray-500/20',
    glow: '',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <rect x="6" y="6" width="12" height="12" rx="2" />
      </svg>
    ),
  },
  out_of_credits: {
    label: 'Out of Credits',
    gradient: 'from-blue-400 via-cyan-400 to-purple-400',
    bg: 'bg-cyan-500/10',
    text: 'text-cyan-500',
    ring: 'ring-cyan-500/20',
    glow: 'shadow-[0_0_20px_rgba(6,182,212,0.15)]',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  cancelled: {
    label: 'Cancelled',
    gradient: 'from-gray-400 to-gray-500',
    bg: 'bg-gray-500/10',
    text: 'text-gray-400',
    ring: 'ring-gray-500/20',
    glow: '',
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <rect x="6" y="6" width="12" height="12" rx="2" />
      </svg>
    ),
  },
  running: {
    label: 'Running',
    gradient: 'from-amber-400 to-yellow-500',
    bg: 'bg-amber-500/10',
    text: 'text-[#FFE100]',
    ring: 'ring-amber-500/20',
    glow: 'shadow-[0_0_20px_rgba(255,225,0,0.12)]',
    icon: (
      <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  pending: {
    label: 'Queued',
    gradient: 'from-amber-400 to-yellow-500',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    ring: 'ring-amber-500/20',
    glow: 'shadow-[0_0_20px_rgba(245,158,11,0.1)]',
    icon: (
      <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
};

const RunDetailsModal = ({ automation, runId, planName, onClose, onNavigate, onEdit }) => {
  const navigate = useNavigate();
  const isDev = process.env.NODE_ENV === 'development';
  const [runDetails, setRunDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [timingCopied, setTimingCopied] = useState(false);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 120);
  };

  const loadRunDetails = async (isBackgroundPoll = false) => {
    if (!isBackgroundPoll) setLoading(true);
    try {
      const details = await cloudApi.getAutomationRunDetails(runId);
      setRunDetails(details);
    } catch (error) {
      console.error("Failed to load run details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRunDetails(); }, [runId]);

  useEffect(() => {
    if (runDetails?.status === 'running' || runDetails?.status === 'pending') {
      const interval = setInterval(() => loadRunDetails(true), 3000);
      return () => clearInterval(interval);
    }
  }, [runDetails?.status, runId]);

  // ── Formatters ───────────────────────────────────────────────────
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatDuration = (startTime, endTime) => {
    const duration = new Date(endTime) - new Date(startTime);
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatPct = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return `${value.toFixed(1)}%`;
  };

  const formatSeconds = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) return '0s';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.round(totalSeconds % 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const parseInsufficientCreditsError = (error) => {
    const match = error?.match(/Insufficient credits\. Required: (\d+), Available: (\d+)/);
    if (match) return { required: parseInt(match[1]), available: parseInt(match[2]) };
    return null;
  };

  const shouldShowMetadata = (key, value) => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'boolean' && !value) return false;
    if (value === 'none' || value === 'None') return false;
    return true;
  };

  const formatMetadataValue = (key, value) => {
    if (typeof value === 'boolean') return value ? 'Yes' : null;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const isUrl = (value) => typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));
  const truncate = (str, maxLen = 60) => str.length > maxLen ? str.substring(0, maxLen) + '...' : str;

  const isProxyError = (msg) => !!(msg && /net::ERR_TUNNEL_CONNECTION_FAILED|Proxy verification failed/i.test(msg));

  const cleanErrorMessage = (msg) => {
    if (!msg) return msg;
    if (isProxyError(msg)) {
      return 'Proxy failed. Try switching proxy location and try again.';
    }
    return msg.replace(/^🤖\s*/, '').replace(/^Page blocked:\s*\w+\s*-\s*/, 'Blocked: ');
  };

  const isRunning = runDetails?.status === 'running' || runDetails?.status === 'pending';
  const isCreditExhausted = runDetails?.status === 'stopped' && runDetails?.stoppedReason === 'insufficient_credits';
  const isContactExtraction = automation?.type === 'extract_emails' || automation?.type === 'extract_social_links';
  const isSocial = automation?.type === 'extract_social_links';
  const typeConfig = AUTOMATION_TYPES[TYPE_STRING_TO_UI_KEY[automation?.type]];
  const statusConfig = isCreditExhausted
    ? STATUS_CONFIG.out_of_credits
    : (STATUS_CONFIG[runDetails?.status] || STATUS_CONFIG.pending);
  const timingReport = runDetails?.timingReport || null;
  const timingSteps = Array.isArray(timingReport?.steps) ? timingReport.steps : [];
  const timingStepsShown = timingSteps.slice(0, 12);
  const timingPerUrl = Array.isArray(timingReport?.perUrl?.entries) ? timingReport.perUrl.entries : [];
  const timingTopUrls = [...timingPerUrl]
    .sort((a, b) => (b.durationMs || 0) - (a.durationMs || 0))
    .slice(0, 5);

  const handleCopyTiming = async () => {
    if (!timingReport) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(timingReport, null, 2));
      setTimingCopied(true);
      setTimeout(() => setTimingCopied(false), 1500);
    } catch (error) {
      console.error("Failed to copy timing report:", error);
    }
  };

  return (
    <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-120 ${closing ? 'opacity-0' : 'opacity-100'}`} onClick={handleClose}>
      <div
        className={`bg-white dark:bg-[#0c0c0f] rounded-2xl border border-gray-200 dark:border-white/[0.08] shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden ${closing ? 'transition-all duration-120 scale-[0.98] opacity-0' : 'animate-modal-in'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Gradient accent line ─────────────────────────────────── */}
        <div className="h-[2px] w-full bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 opacity-60" />

        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="px-5 py-3 flex items-center justify-between flex-shrink-0 border-b border-gray-100 dark:border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            {typeConfig && (() => {
              const TypeIcon = typeConfig.icon;
              const cc = typeConfig.colorClasses;
              return (
                <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${cc?.iconTileBg || "bg-gray-400/20"}`}
                  title={typeConfig.name}>
                  <TypeIcon className={`w-3.5 h-3.5 ${cc?.text || "text-gray-400 dark:text-gray-500"}`} />
                </div>
              );
            })()}
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap flex-shrink-0">Run Details</h2>
            <span className="text-xs text-gray-400 dark:text-gray-600 truncate min-w-0">{automation.name}</span>
          </div>
          <div className="flex items-center gap-0.5">
            <button onClick={() => onNavigate('prev')}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              title="Previous run">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={() => onNavigate('next')}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              title="Next run">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="w-px h-4 bg-gray-200 dark:bg-white/[0.08] mx-1.5" />
            <button onClick={handleClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border-2 border-gray-200 dark:border-white/[0.06]" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#FFE100] animate-spin" />
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">Loading run details...</span>
            </div>
          ) : runDetails ? (
            <div className="p-5 space-y-4">

              {/* ── Status Hero ──────────────────────────────────── */}
              <Section delay={0}>
                <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3.5 min-w-0">
                    <div className={`flex-shrink-0 p-2.5 rounded-xl ${statusConfig.bg} ${statusConfig.text} ${statusConfig.glow} ring-1 ${statusConfig.ring} animate-scale-up`}>
                      {statusConfig.icon}
                    </div>
                    <div className="min-w-0 pt-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-gray-900 dark:text-white">
                          {statusConfig.label}
                        </span>
                      </div>
                      {runDetails.message && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">{runDetails.message}</p>
                      )}
                    </div>
                  </div>
                  {runDetails.startTime && (
                    <div className="flex-shrink-0 text-right animate-count-up">
                      <div className="text-2xl font-light font-mono text-gray-900 dark:text-white tabular-nums tracking-tight">
                        {formatDuration(runDetails.startTime, runDetails.endTime || Date.now())}
                      </div>
                      <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">duration</div>
                    </div>
                  )}
                </div>

                {/* ── Metric pills ───────────────────────────────── */}
                <div className="px-5 py-4 border-t border-gray-100 dark:border-white/[0.04]">
                  <div className="grid grid-cols-2 sm:flex sm:items-end gap-4 sm:gap-6">
                    {isContactExtraction ? (
                      <>
                        {/* Contact extraction hero — emails (email task) or socials (social task).
                            Either task can have the other half enabled, so the counterpart
                            shows alongside it whenever the run actually found any. */}
                        {isSocial ? (
                          <>
                            <div className="animate-stagger-in" style={{ animationDelay: '50ms' }}>
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-3xl font-light text-cyan-600 dark:text-cyan-400 tabular-nums tracking-tight">
                                  <AnimatedNumber value={runDetails.subPageScan?.socialFound || 0} format={formatNumber} />
                                </span>
                                {isRunning && runDetails.subPageScan?.socialFound > 0 && (
                                  <span className="ml-0.5 inline-block w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                                )}
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">social links found</div>
                            </div>

                            {runDetails.subPageScan?.emailsFound > 0 && (
                              <>
                                <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/[0.06]" />
                                <div className="animate-stagger-in" style={{ animationDelay: '100ms' }}>
                                  <div className="text-xl font-light text-emerald-600 dark:text-emerald-400 tabular-nums">
                                    <AnimatedNumber value={runDetails.subPageScan.emailsFound} format={formatNumber} />
                                  </div>
                                  <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">emails</div>
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="animate-stagger-in" style={{ animationDelay: '50ms' }}>
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-3xl font-light text-emerald-600 dark:text-emerald-400 tabular-nums tracking-tight">
                                  <AnimatedNumber value={runDetails.subPageScan?.emailsFound || 0} format={formatNumber} />
                                </span>
                                {isRunning && runDetails.subPageScan?.emailsFound > 0 && (
                                  <span className="ml-0.5 inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                )}
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">emails extracted</div>
                            </div>

                            {runDetails.subPageScan?.socialFound > 0 && (
                              <>
                                <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/[0.06]" />
                                <div className="animate-stagger-in" style={{ animationDelay: '100ms' }}>
                                  <div className="text-xl font-light text-cyan-600 dark:text-cyan-400 tabular-nums">
                                    <AnimatedNumber value={runDetails.subPageScan.socialFound} format={formatNumber} />
                                  </div>
                                  <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">social links</div>
                                </div>
                              </>
                            )}
                          </>
                        )}

                        <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/[0.06]" />
                        <div className="animate-stagger-in" style={{ animationDelay: '150ms' }}>
                          <div className="text-xl font-light text-gray-900 dark:text-white tabular-nums">
                            <AnimatedNumber value={runDetails.rowCount || 0} format={formatNumber} />
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">rows</div>
                        </div>

                        {runDetails.creditsConsumed != null && (
                          <>
                            <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/[0.06]" />
                            <div className="animate-stagger-in" style={{ animationDelay: '200ms' }}>
                              <div className="text-xl font-light text-gray-900 dark:text-white tabular-nums">
                                <AnimatedNumber value={runDetails.creditsConsumed} format={formatNumber} />
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">credits</div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Rows - hero metric */}
                        <div className="animate-stagger-in" style={{ animationDelay: '50ms' }}>
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-3xl font-light text-gray-900 dark:text-white tabular-nums tracking-tight">
                              <AnimatedNumber value={runDetails.rowCount || 0} format={formatNumber} />
                            </span>
                            {isRunning && runDetails.rowCount > 0 && (
                              <span className="ml-0.5 inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            )}
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">rows extracted</div>
                        </div>

                        {runDetails.creditsConsumed != null && (
                          <>
                            <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/[0.06]" />
                            <div className="animate-stagger-in" style={{ animationDelay: '100ms' }}>
                              <div className="text-xl font-light text-gray-900 dark:text-white tabular-nums">
                                <AnimatedNumber value={runDetails.creditsConsumed} format={formatNumber} />
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">credits</div>
                            </div>
                          </>
                        )}

                        {runDetails.network && (
                          <>
                            <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/[0.06]" />
                            <div className="animate-stagger-in" style={{ animationDelay: '150ms' }}>
                              <div className="text-xl font-light text-gray-900 dark:text-white tabular-nums font-mono">
                                {runDetails.network.dataConsumed || formatBytes(runDetails.network.bytesReceived)}
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">data</div>
                            </div>
                            <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-white/[0.06]" />
                            <div className="animate-stagger-in" style={{ animationDelay: '200ms' }}>
                              <div className="text-xl font-light text-gray-900 dark:text-white tabular-nums">
                                <AnimatedNumber value={runDetails.network.requestCount} format={formatNumber} />
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">requests</div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ── Footer: timestamps + actions ───────────────── */}
                <div className="px-5 py-3 border-t border-gray-100 dark:border-white/[0.04] bg-gray-50/50 dark:bg-white/[0.01] flex items-center justify-between gap-3">
                  <div className="text-[11px] text-gray-400 dark:text-gray-500 font-mono tabular-nums">
                    {formatTimestamp(runDetails.startTime || runDetails.queuedAt)}
                    {runDetails.endTime ? (
                      <span> → {formatTimestamp(runDetails.endTime)}</span>
                    ) : (
                      <span className="text-amber-500 dark:text-amber-400 font-sans font-medium"> · running</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {runDetails.tableId && (
                      <button
                        onClick={() => window.open(`/table/${runDetails.tableId}`, '_blank')}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-[#FFE100] to-[#FFD000] text-gray-900 rounded-lg hover:shadow-[0_0_12px_rgba(255,225,0,0.3)] transition-all text-xs font-semibold"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        View Data
                      </button>
                    )}
                    {runDetails.googleSheetsExport?.spreadsheetUrl && (
                      <a href={runDetails.googleSheetsExport.spreadsheetUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-all text-xs font-medium">
                        <svg className="w-3.5 h-3.5 text-green-600 dark:text-green-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14.727 6.727H14V0H4.91c-.905 0-1.637.732-1.637 1.636v20.728C3.273 23.268 4.005 24 4.91 24h14.182c.904 0 1.636-.732 1.636-1.636V6.727h-6zM7.09 18.545H6.273V15.09H7.09v3.455zm2.454 0h-.817V13.09h.818v5.455zm2.455 0h-.818v-4.09h.818v4.09zm2.454 0h-.817v-2.727h.818v2.727zM14.727 5.91h5.455L14.727.545V5.91z" />
                        </svg>
                        Sheets
                      </a>
                    )}
                    {runDetails.webhookExport?.downloadUrl && (
                      <a href={runDetails.webhookExport.downloadUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg transition-all text-xs font-medium">
                        <svg className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Webhook
                      </a>
                    )}
                  </div>
                </div>
              </Section>

              {/* ── Credit Exhaustion ─────────────────────────────── */}
              {isCreditExhausted && (
                <Section delay={100}>
                  <div className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-500 ring-1 ring-cyan-500/20 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Ran out of credits</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          This automation ran out of credits during extraction. Your partial data has been saved.
                          Top up your credits and resume to continue extracting.
                        </p>
                        <button onClick={() => navigate(!planName || planName === 'free' || planName === 'starter' ? '/plans' : '/topups')}
                          className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 hover:shadow-lg hover:shadow-cyan-500/30 transition-all text-xs font-semibold">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          {!planName || planName === 'free' || planName === 'starter' ? 'Upgrade Plan' : 'Buy Credits'}
                        </button>
                      </div>
                    </div>
                  </div>
                </Section>
              )}

              {/* ── Error Details ─────────────────────────────────── */}
              {runDetails.error && (() => {
                const creditsError = !isCreditExhausted && parseInsufficientCreditsError(runDetails.error);
                if (creditsError) {
                  const shortfall = creditsError.required - creditsError.available;
                  return (
                    <Section delay={100}>
                      <div className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-xl bg-cyan-500/10 text-cyan-500 ring-1 ring-cyan-500/20 flex-shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Not enough credits</h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                              This automation requires {formatNumber(creditsError.required)} credits, but you only have {formatNumber(creditsError.available)} available.
                              You need {formatNumber(shortfall)} more credit{shortfall !== 1 ? 's' : ''} to run this automation.
                            </p>
                            <button onClick={() => navigate(!planName || planName === 'free' || planName === 'starter' ? '/plans' : '/topups')}
                              className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 hover:shadow-lg hover:shadow-cyan-500/30 transition-all text-xs font-semibold">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              {!planName || planName === 'free' || planName === 'starter' ? 'Upgrade Plan' : 'Buy Credits'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </Section>
                  );
                }
                return (
                  <Section delay={100}>
                    <div className="p-4 flex items-start gap-3">
                      <div className="p-1.5 rounded-lg bg-red-500/10 text-red-500 flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1">Error</h4>
                        <p className="text-gray-700 dark:text-gray-300 font-mono text-xs leading-relaxed">{cleanErrorMessage(runDetails.error)}</p>
                        {isProxyError(runDetails.error) && onEdit && (
                          <button
                            onClick={() => { onClose(); onEdit(automation, 'proxyConfiguration'); }}
                            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            Change proxy location
                          </button>
                        )}
                      </div>
                    </div>
                  </Section>
                );
              })()}

              {/* ── Export Errors ──────────────────────────────────── */}
              {((runDetails.googleSheetsExport?.error && !runDetails.googleSheetsExport?.spreadsheetUrl) ||
                (runDetails.webhookExport?.error && !runDetails.webhookExport?.downloadUrl)) && (
                <Section delay={120}>
                  <div className="p-4 space-y-2">
                    {runDetails.googleSheetsExport?.error && !runDetails.googleSheetsExport?.spreadsheetUrl && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Google Sheets export failed: {runDetails.googleSheetsExport.error}
                      </p>
                    )}
                    {runDetails.webhookExport?.error && !runDetails.webhookExport?.downloadUrl && (
                      <p className="text-xs text-red-600 dark:text-red-400">
                        {runDetails.webhookExport.error}
                      </p>
                    )}
                  </div>
                </Section>
              )}

              {/* ── Screenshots ───────────────────────────────────── */}
              {runDetails.screenshotUrls && runDetails.screenshotUrls.length > 0 && (
                <Section delay={150}>
                  <SectionHeader>Screenshots ({runDetails.screenshotUrls.length})</SectionHeader>
                  <div className="px-5 pb-5">
                    <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
                      {runDetails.screenshotUrls.map((url, index) => (
                        <button
                          key={index}
                          onClick={() => setGalleryInitialIndex(index)}
                          className={`flex-shrink-0 relative group rounded-xl transition-all duration-300 overflow-hidden ${
                            galleryInitialIndex === index
                              ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-white dark:ring-offset-[#0c0c0f] shadow-glow-cyan scale-[1.02]'
                              : 'ring-1 ring-gray-200 dark:ring-white/[0.08] hover:ring-gray-300 dark:hover:ring-white/[0.15] hover:shadow-lg hover:scale-[1.03]'
                          }`}
                          style={{ width: '130px', height: '82px', animationDelay: `${index * 50}ms` }}
                        >
                          {!loadedImages.has(index) && (
                            <div className="absolute inset-0 bg-gray-100 dark:bg-white/[0.04] animate-pulse flex items-center justify-center">
                              <svg className="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                          <img src={cfThumbnail(url)} alt={`Screenshot ${index + 1}`}
                            className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-110 ${loadedImages.has(index) ? 'opacity-100' : 'opacity-0'}`}
                            onLoad={() => setLoadedImages(prev => new Set([...prev, index]))}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-2">
                            <svg className="w-5 h-5 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </Section>
              )}

              {/* ── URL Processing Progress (unified with sub-page scan for email tasks) ── */}
              {runDetails.catalogue && (runDetails.catalogue.summary || runDetails.catalogue.products_listed != null) && (
                <Section delay={180}>
                  <SectionHeader>Catalogue</SectionHeader>
                  <div className="px-5 pb-5 space-y-3">
                    {runDetails.catalogue.hint && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{runDetails.catalogue.hint}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {runDetails.catalogue.phase_label && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/10">
                          {runDetails.catalogue.phase_label}
                        </span>
                      )}
                      {runDetails.catalogue.products_listed != null && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border border-gray-200/70 dark:border-white/[0.06]">
                          Listed
                          <span className="tabular-nums font-medium">
                            {formatNumber(runDetails.catalogue.products_listed)}
                            {runDetails.catalogue.products_estimated ? ` of ~${formatNumber(runDetails.catalogue.products_estimated)}` : ''}
                          </span>
                        </span>
                      )}
                      {runDetails.catalogue.variant_rows != null && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border border-gray-200/70 dark:border-white/[0.06]">
                          Variant rows
                          <span className="tabular-nums font-medium">{formatNumber(runDetails.catalogue.variant_rows)}</span>
                        </span>
                      )}
                      {runDetails.catalogue.page != null && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border border-gray-200/70 dark:border-white/[0.06]">
                          Page
                          <span className="tabular-nums font-medium">
                            {runDetails.catalogue.page}
                            {runDetails.catalogue.pages_estimated ? ` of ~${runDetails.catalogue.pages_estimated}` : ''}
                          </span>
                        </span>
                      )}
                      {runDetails.catalogue.variations_total > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border border-gray-200/70 dark:border-white/[0.06]">
                          Variations
                          <span className="tabular-nums font-medium">
                            {formatNumber(runDetails.catalogue.variations_done || 0)} of {formatNumber(runDetails.catalogue.variations_total)}
                          </span>
                        </span>
                      )}
                      {runDetails.catalogue.rate && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border border-gray-200/70 dark:border-white/[0.06]">
                          {runDetails.catalogue.rate}
                        </span>
                      )}
                      {runDetails.catalogue.scope && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-300 border border-gray-200/70 dark:border-white/[0.06]">
                          {runDetails.catalogue.scope}
                        </span>
                      )}
                    </div>
                  </div>
                </Section>
              )}

              {runDetails.urlStats && (runDetails.urlStats.total > 0 || runDetails.recentActivity?.length > 0) && (() => {
                const urlStats = runDetails.urlStats;
                const scan = runDetails.subPageScan;
                const showScanSummary = isContactExtraction && scan && (scan.pagesScannedTotal || 0) > 0;
                const pendingTotal = urlStats.pending + urlStats.inProgress + (urlStats.retrying || 0);
                const completedPct = urlStats.total > 0 ? (urlStats.completed / urlStats.total) * 100 : 0;
                const blockedPct = urlStats.total > 0 ? (urlStats.blocked / urlStats.total) * 100 : 0;
                const failedPct = urlStats.total > 0 ? (urlStats.failed / urlStats.total) * 100 : 0;
                const activePct = urlStats.total > 0 ? (urlStats.inProgress / urlStats.total) * 100 : 0;
                // A blocked URL waits in RETRYING until its retry budget is spent. Without
                // its own segment it renders the same as a URL we have not touched, so a
                // heavily blocked run shows an almost empty bar while most of the list has
                // already been tried once.
                const retryingPct = urlStats.total > 0 ? ((urlStats.retrying || 0) / urlStats.total) * 100 : 0;

                return (
                  <Section delay={200}>
                    <div className="p-5">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-baseline gap-2.5">
                          <span className="text-2xl font-light text-gray-900 dark:text-white tabular-nums tracking-tight">
                            <AnimatedNumber value={urlStats.processed} format={formatNumber} />
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            / {urlStats.total} {showScanSummary ? 'main pages' : 'URLs'} processed
                          </span>
                        </div>
                        {isRunning && runDetails.batchNumber && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium">
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Batch {runDetails.batchNumber}
                            {runDetails.avgTabs != null && <span className="text-blue-400/60"> · {runDetails.avgTabs} tabs</span>}
                          </div>
                        )}
                      </div>

                      {/* Main → sub-page relationship */}
                      {showScanSummary && scan.enabled && scan.maxDepth ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                          Each main page crawled to depth {scan.maxDepth}
                        </p>
                      ) : null}

                      {/* Segmented progress bar */}
                      <div className="relative h-2 rounded-full bg-gray-100 dark:bg-white/[0.04] overflow-hidden mb-4">
                        {urlStats.completed > 0 && (
                          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700 ease-out rounded-full"
                            style={{ width: `${completedPct}%` }} />
                        )}
                        {urlStats.blocked > 0 && (
                          <div className="absolute inset-y-0 bg-gradient-to-r from-orange-400 to-amber-400 transition-all duration-700 ease-out"
                            style={{ left: `${completedPct}%`, width: `${blockedPct}%` }} />
                        )}
                        {urlStats.failed > 0 && (
                          <div className="absolute inset-y-0 bg-gradient-to-r from-red-400 to-rose-400 transition-all duration-700 ease-out"
                            style={{ left: `${completedPct + blockedPct}%`, width: `${failedPct}%` }} />
                        )}
                        {urlStats.retrying > 0 && (
                          <div className="absolute inset-y-0 bg-amber-400/40 transition-all duration-700 ease-out"
                            title={`${urlStats.retrying} blocked, queued to retry`}
                            style={{ left: `${completedPct + blockedPct + failedPct}%`, width: `${retryingPct}%` }} />
                        )}
                        {isRunning && urlStats.inProgress > 0 && (
                          <div className="absolute inset-y-0 bg-blue-400/30 transition-all duration-700 ease-out overflow-hidden"
                            style={{ left: `${completedPct + blockedPct + failedPct + retryingPct}%`, width: `${activePct}%` }}>
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                          </div>
                        )}
                      </div>

                      {/* Status pills */}
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {urlStats.completed} completed
                        </span>
                        {showScanSummary && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                            {formatNumber(scan.pagesScannedTotal || 0)} pages scanned
                          </span>
                        )}
                        {urlStats.blocked > 0 && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-500/10 text-orange-600 dark:text-orange-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                            {urlStats.blocked} blocked
                          </span>
                        )}
                        {urlStats.failed > 0 && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-500/10 text-red-600 dark:text-red-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                            {urlStats.failed} failed
                          </span>
                        )}
                        {pendingTotal > 0 && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-500/10 text-gray-500 dark:text-gray-400">
                            <span className={`w-1.5 h-1.5 rounded-full ${isRunning && urlStats.inProgress > 0 ? 'bg-blue-400 animate-pulse' : 'bg-gray-400'}`} />
                            {pendingTotal} {isRunning ? `remaining${urlStats.inProgress > 0 ? ` · ${urlStats.inProgress} active` : ''}` : 'not attempted'}
                          </span>
                        )}
                      </div>

                      {/* Live scanning indicator (email tasks) */}
                      {isContactExtraction && isRunning && scan?.currentUrl && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.06] flex items-center gap-2">
                          <span className="relative flex h-2 w-2 flex-shrink-0">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-500" />
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Scanning</span>
                          <span className="truncate font-mono text-xs text-gray-700 dark:text-gray-300" title={scan.currentUrl}>
                            {truncateUrl(scan.currentUrl)}
                          </span>
                        </div>
                      )}

                      {/* Page Unblocker Recoveries */}
                      {runDetails.recoverySummary && runDetails.recoverySummary.total > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                          <div className="flex items-center gap-2 mb-2.5">
                            <div className="p-1 rounded-md bg-emerald-500/10">
                              <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            </div>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              Page Unblocker rescued {runDetails.recoverySummary.total} URL{runDetails.recoverySummary.total !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="space-y-1.5 ml-7">
                            {Object.entries(runDetails.recoverySummary.byType).map(([type, data]) => (
                              <div key={type} className="flex items-start gap-1.5">
                                <svg className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  {data.message || `Resolved ${data.label || type}`}
                                  {data.count > 1 && <span className="text-gray-400 dark:text-gray-500"> ({data.count} URLs)</span>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Block Summary */}
                      {/* Throttling — explains why a run is crawling. Without this a
                          rate-limited run looks identical to a slow one. */}
                      {runDetails.throttle?.throttled && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                          <div className="flex items-center gap-2 mb-2.5">
                            <div className="p-1 rounded-md bg-amber-500/10">
                              <svg className="w-3.5 h-3.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {runDetails.throttle.rateLimited
                                ? <>Slowed down &mdash; <span className="text-amber-600 dark:text-amber-400">{runDetails.throttle.domain}</span> is rate limiting us</>
                                : <>Slowed down &mdash; <span className="text-amber-600 dark:text-amber-400">{runDetails.throttle.domain}</span> is blocking most requests</>}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 ml-7">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/10">
                              Parallel pages
                              <span className="tabular-nums font-medium">{runDetails.throttle.tabs} / {runDetails.throttle.initialTabs}</span>
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/10">
                              Wait between requests
                              <span className="tabular-nums font-medium">{(runDetails.throttle.delayMs / 1000).toFixed(1)}s</span>
                            </span>
                            {runDetails.throttle.blockRate > 0 && (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/10">
                                Recently blocked
                                <span className="tabular-nums font-medium">{Math.round(runDetails.throttle.blockRate * 100)}%</span>
                              </span>
                            )}
                          </div>
                          <p className="mt-2 ml-7 text-xs text-gray-500 dark:text-gray-500">
                            We back off automatically so more pages get through. This run will take longer than usual.
                          </p>
                        </div>
                      )}

                      {runDetails.blockSummary && runDetails.blockSummary.total > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                          <div className="flex items-center gap-2 mb-2.5">
                            <div className="p-1 rounded-md bg-orange-500/10">
                              <svg className="w-3.5 h-3.5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {runDetails.blockSummary.total} URL{runDetails.blockSummary.total !== 1 ? 's' : ''} blocked
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 ml-7">
                            {Object.entries(runDetails.blockSummary.byType).map(([type, data]) => (
                              <div key={type}>
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/10"
                                  title={data.urls.join('\n')}>
                                  {data.label || type}
                                  <span className="text-orange-400/60 dark:text-orange-500 tabular-nums font-medium">{data.count}</span>
                                </span>
                                {data.error && (
                                  <p className="mt-0.5 ml-1 text-xs text-gray-500 dark:text-gray-500 truncate" title={data.error}>{data.error}</p>
                                )}
                              </div>
                            ))}
                          </div>
                          {isRunning && runDetails.blockSummary.retrying > 0 && (
                            <p className="mt-2 ml-7 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              {runDetails.blockSummary.retrying} queued for retry
                            </p>
                          )}
                        </div>
                      )}

                      {/* Live activity feed */}
                      {isRunning && runDetails.recentActivity?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                          <div className="space-y-0.5" style={{ contain: 'content' }}>
                            {runDetails.recentActivity.map((item) => (
                              <ActivityRow key={item.index} item={item} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Completed summary with donut */}
                      {!isRunning && urlStats.total > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.06] flex items-center gap-4">
                          <ProgressRing percent={completedPct} size={48} strokeWidth={3.5}
                            color={completedPct === 100 ? 'text-emerald-400' : completedPct > 70 ? 'text-blue-400' : 'text-amber-400'} />
                          <div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              {urlStats.completed} of {urlStats.total} URLs completed
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {urlStats.blocked === 0 && urlStats.failed === 0 && pendingTotal === 0
                                ? (runDetails.recoverySummary?.total > 0
                                  ? `All URLs successful · ${runDetails.recoverySummary.total} rescued by Page Unblocker`
                                  : 'All URLs successful')
                                : [
                                    urlStats.blocked > 0 && `${urlStats.blocked} blocked`,
                                    urlStats.failed > 0 && `${urlStats.failed} failed`,
                                    pendingTotal > 0 && `${pendingTotal} not attempted`,
                                    runDetails.recoverySummary?.total > 0 && `${runDetails.recoverySummary.total} rescued`,
                                  ].filter(Boolean).join(' · ')
                              }
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </Section>
                );
              })()}

              {/* ── Steps Timeline ────────────────────────────────── */}
              {runDetails.journal && runDetails.journal.length > 0 && (
                <Section delay={250}>
                  <SectionHeader
                    right={
                      <div className="flex items-center gap-2">
                        {runDetails.journalSummary && (
                          <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                            {runDetails.journalSummary.successSteps}/{runDetails.journalSummary.totalSteps} · {formatSeconds(runDetails.journalSummary.totalDuration)}
                          </span>
                        )}
                        {isRunning && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/10 text-emerald-500 text-[11px] font-semibold rounded-full">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                            </span>
                            LIVE
                          </span>
                        )}
                      </div>
                    }
                  >
                    Steps
                  </SectionHeader>

                  <div className="px-5 pb-5">
                    <div className="relative">
                      {/* Connecting line — centered on the 18px dot column */}
                      <div className="absolute left-[8.5px] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gray-200 dark:via-white/[0.06] to-transparent z-0" />

                      <div className="space-y-0.5">
                        {runDetails.journal.map((step, index) => {
                          const dotBg = step.status === 'success' ? 'bg-emerald-500'
                            : step.status === 'error' ? 'bg-red-500'
                            : step.status === 'running' ? 'bg-blue-400'
                            : 'bg-gray-300 dark:bg-gray-600';

                          return (
                            <div key={index}
                              className="relative flex items-start gap-3 py-2 animate-stagger-in"
                              style={{ animationDelay: `${300 + index * 40}ms` }}>
                              {/* Timeline dot — fixed 18px box, centered on the line */}
                              <div className="relative z-10 flex-shrink-0 w-[18px] h-[18px] flex items-center justify-center mt-0.5">
                                {step.status === 'running' ? (
                                  <>
                                    <span className="absolute w-[18px] h-[18px] rounded-full bg-blue-400/20 animate-ping-slow" />
                                    <span className="relative w-2.5 h-2.5 rounded-full bg-blue-400 ring-2 ring-white dark:ring-[#0c0c0f]" />
                                  </>
                                ) : step.status === 'success' ? (
                                  <div className="w-[18px] h-[18px] rounded-full bg-emerald-500/15 ring-2 ring-white dark:ring-[#0c0c0f] flex items-center justify-center">
                                    <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                ) : step.status === 'error' ? (
                                  <div className="w-[18px] h-[18px] rounded-full bg-red-500/15 ring-2 ring-white dark:ring-[#0c0c0f] flex items-center justify-center">
                                    <svg className="w-2.5 h-2.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </div>
                                ) : (
                                  <span className={`w-2 h-2 rounded-full ${dotBg} ring-2 ring-white dark:ring-[#0c0c0f]`} />
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0 pb-1">
                                <div className="flex items-center justify-between gap-2">
                                  <span className={`text-sm ${step.status === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                                    {step.description}
                                  </span>
                                  {step.duration != null && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500 font-mono flex-shrink-0 tabular-nums">
                                      {step.duration.toFixed(2)}s
                                    </span>
                                  )}
                                </div>
                                {step.metadata && Object.keys(step.metadata).length > 0 && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                    {Object.entries(step.metadata)
                                      .filter(([key, value]) => shouldShowMetadata(key, value))
                                      .map(([key, value]) => {
                                        const formatted = formatMetadataValue(key, value);
                                        if (formatted === null) return null;
                                        const label = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase());
                                        const isSpreadsheetLink = key === 'Spreadsheet' && isUrl(value);
                                        const isUrlValue = !isSpreadsheetLink && isUrl(String(value));
                                        return (
                                          <span key={key} className="break-all">
                                            <span className="text-gray-400 dark:text-gray-500">{label}:</span>{' '}
                                            {isSpreadsheetLink ? (
                                              <a href={value} target="_blank" rel="noopener noreferrer"
                                                className="text-blue-500 dark:text-blue-400 hover:underline">Open</a>
                                            ) : isUrlValue ? (
                                              <span className="text-gray-600 dark:text-gray-300 cursor-help" title={String(value)}>
                                                {truncate(String(value))}
                                              </span>
                                            ) : (
                                              <span className="text-gray-600 dark:text-gray-300">{formatted}</span>
                                            )}
                                          </span>
                                        );
                                      })}
                                  </div>
                                )}
                                {step.error && (
                                  <div className="text-xs text-red-500 dark:text-red-400 mt-1.5 font-mono leading-relaxed">
                                    {cleanErrorMessage(step.error)}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </Section>
              )}

              {/* ── Timing Report (Dev) ──────────────────────────── */}
              {isDev && timingReport && timingSteps.length > 0 && (
                <Section delay={300}>
                  <SectionHeader
                    right={
                      <div className="flex items-center gap-3">
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono tabular-nums">
                          Wall: {formatMs(timingReport.wallClockMs)}
                        </span>
                        <button onClick={handleCopyTiming}
                          className={`text-[11px] font-medium px-2.5 py-1 rounded-md transition-all ${
                            timingCopied
                              ? 'bg-emerald-500/10 text-emerald-500'
                              : 'text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]'
                          }`}>
                          {timingCopied ? 'Copied!' : 'Copy JSON'}
                        </button>
                      </div>
                    }
                  >
                    Timing (Dev)
                  </SectionHeader>

                  {timingReport.note && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 px-5 mb-3">{timingReport.note}</p>
                  )}

                  {/* Visual bars for top steps */}
                  <div className="px-5 pb-4 space-y-2">
                    {timingStepsShown.slice(0, 6).map((step, i) => (
                      <HorizontalBar
                        key={step.name}
                        label={step.name}
                        value={step.totalMs}
                        max={timingStepsShown[0]?.totalMs || 1}
                        delay={i * 80}
                        color={i === 0 ? 'from-cyan-400 to-blue-500' : i === 1 ? 'from-blue-400 to-indigo-500' : 'from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500'}
                      />
                    ))}
                  </div>

                  {/* Detail table */}
                  <div className="px-5 pb-5 overflow-x-auto">
                    <table className="min-w-[700px] w-full text-xs">
                      <thead>
                        <tr className="text-gray-400 dark:text-gray-500 border-b border-gray-200/60 dark:border-white/[0.06]">
                          <th className="text-left font-medium py-2 pr-2">Step</th>
                          <th className="text-right font-medium py-2 px-2">Count</th>
                          <th className="text-right font-medium py-2 px-2">Total</th>
                          <th className="text-right font-medium py-2 px-2">Avg</th>
                          <th className="text-right font-medium py-2 px-2">Min</th>
                          <th className="text-right font-medium py-2 px-2">Max</th>
                          <th className="text-right font-medium py-2 pl-2">% Wall</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timingStepsShown.map((step) => (
                          <tr key={step.name} className="border-b border-gray-100 dark:border-white/[0.03] hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                            <td className="py-2 pr-2 text-gray-800 dark:text-gray-200 font-mono">{step.name}</td>
                            <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400">{formatNumber(step.count)}</td>
                            <td className="py-2 px-2 text-right text-gray-900 dark:text-white font-mono font-medium">{formatMs(step.totalMs)}</td>
                            <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-mono">{formatMs(step.avgMs)}</td>
                            <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-mono">{formatMs(step.minMs)}</td>
                            <td className="py-2 px-2 text-right text-gray-500 dark:text-gray-400 font-mono">{formatMs(step.maxMs)}</td>
                            <td className="py-2 pl-2 text-right text-gray-500 dark:text-gray-400 font-mono">{formatPct(step.pctWallClock)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {timingSteps.length > timingStepsShown.length && (
                    <p className="text-xs text-gray-400 px-5 pb-4">
                      Showing top {timingStepsShown.length} of {timingSteps.length} steps.
                    </p>
                  )}

                  {timingTopUrls.length > 0 && (
                    <div className="mx-5 mb-5 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Slow URLs</span>
                        {timingReport.perUrl?.truncated && (
                          <span className="text-[11px] text-gray-400">Sampled {timingReport.perUrl.sampled} of {timingReport.perUrl.count}</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        {timingTopUrls.map((entry) => (
                          <div key={`${entry.index}-${entry.url}`}
                            className="bg-gray-50 dark:bg-white/[0.02] border border-gray-100 dark:border-white/[0.04] rounded-lg p-3">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-xs font-mono text-gray-600 dark:text-gray-300 font-medium">{formatMs(entry.durationMs)}</span>
                              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                                entry.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                : entry.status === 'failed' ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                                : entry.status === 'blocked' ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                                : 'bg-gray-500/10 text-gray-600 dark:text-gray-300'
                              }`}>
                                {entry.status}
                              </span>
                            </div>
                            <div className="mt-1.5 text-xs text-gray-600 dark:text-gray-300 font-mono truncate" title={entry.url}>
                              {entry.url}
                            </div>
                            {entry.timing && (
                              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500 font-mono">
                                {entry.timing["tab.load_url"] !== undefined && <span>load {formatMs(entry.timing["tab.load_url"])}</span>}
                                {entry.timing["tab.extract"] !== undefined && <span>extract {formatMs(entry.timing["tab.extract"])}</span>}
                                {entry.timing["tab.unblocker"] !== undefined && <span>unblock {formatMs(entry.timing["tab.unblocker"])}</span>}
                                {entry.timing["tab.persist"] !== undefined && <span>persist {formatMs(entry.timing["tab.persist"])}</span>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {/* ── Image Gallery ─────────────────────────────────── */}
              {galleryInitialIndex !== null && runDetails.screenshotUrls && (
                <ImageGalleryModal
                  screenshots={runDetails.screenshotUrls}
                  title="Screenshots"
                  onClose={() => setGalleryInitialIndex(null)}
                  initialIndex={galleryInitialIndex}
                />
              )}

            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <svg className="w-8 h-8 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm text-gray-500 dark:text-gray-400">Failed to load run details</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunDetailsModal;
